/**
 * Rohdaten — Rohdaten-Explorer mit tabellarischer Ansicht aller Datenquellen.
 *
 * Features:
 * - 10 Tabs: Billing, Waves, Packs, Forecast, Anwesenheiten, Buchhaltung, Mitarbeiter, Schichtplanung, Kunden
 * - Sortierung, Filterung, Paginierung und CSV-Export pro Tab
 * - Datenquellen-Übersicht (eigene DB vs. Odoo)
 *
 * @page /rohdaten
 * @roles admin
 * @i18n rawData, common
 * @endpoints GET /api/rohdaten, /api/rohdaten/overview, /api/rohdaten/kunden, /api/rohdaten/auftraege, /api/rohdaten/billing, /api/rohdaten/waves, /api/rohdaten/packs, /api/rohdaten/buchhaltung, /api/rohdaten/mitarbeiter-all, /api/rohdaten/schichtplanung, /api/rohdaten/filter-options, /api/rohdaten/kunden-alle, /api/odoo/anwesenheiten
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { Users, PackageCheck, PackageX, Camera } from 'lucide-react';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path) {
  return fetch(path).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
    return data;
  });
}

function fmt(n) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE');
}

function fmtEur(n) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDt(s) {
  if (!s) return '–';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
      d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

function fmtDate(s) {
  if (!s) return '–';
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return s; }
}

function today() { return new Date().toISOString().slice(0, 10); }
function monthStart() { return today().slice(0, 8) + '01'; }

// ─── Shared UI ─────────────────────────────────────────────────────

function SortIcon({ active, dir }) {
  if (!active) return <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="inline ml-1 opacity-25"><path d="M6 2l3 4H3z" fill="currentColor" /><path d="M6 10l3-4H3z" fill="currentColor" /></svg>;
  return <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="inline ml-1">{dir === 'asc' ? <path d="M6 2l3 4H3z" fill="currentColor" /> : <path d="M6 10l3-4H3z" fill="currentColor" />}</svg>;
}

function SourceBadge({ type }) {
  if (type === 'db') return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-primary-500/15 text-primary-400 border border-primary-500/20">DB Eigene DB</span>;
  return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-orange-500/15 text-orange-400 border border-orange-500/20">Odoo XMLRPC <span className="text-[9px] opacity-60">READ-ONLY</span></span>;
}

function FilterInput({ type = 'text', ...props }) {
  return <input type={type} {...props} className="px-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content-secondary focus:outline-none focus:border-line-hover placeholder-content-dim" />;
}

function FilterSelect({ children, ...props }) {
  return <select {...props} className="px-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content-secondary focus:outline-none focus:border-line-hover">{children}</select>;
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="pl-8 pr-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content placeholder-content-dim focus:outline-none focus:border-line-hover w-52" />
    </div>
  );
}

function RefreshBtn({ onClick, loading, t }) {
  return (
    <button onClick={onClick} disabled={loading}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content-tertiary hover:text-content hover:bg-surface-active transition-colors disabled:opacity-40">
      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"
        className={loading ? 'animate-spin' : ''}>
        <path d="M4 4v5h5M20 20v-5h-5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.49 9A9 9 0 005.64 5.64L4 4m16 16l-1.64-1.64A9 9 0 013.51 15" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('common.refresh')}
    </button>
  );
}

function ExportBtn({ onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/15 border border-indigo-500/20 rounded-lg text-xs text-indigo-300 hover:bg-indigo-600/25 transition-colors">
      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      CSV
    </button>
  );
}

function TabMeta({ total, updatedAt, t }) {
  return (
    <div className="flex items-center gap-3 text-[11px] text-[var(--text-secondary)]">
      <span className="font-mono">{total != null ? fmt(total) : '–'} {t('rawData.entries')}</span>
      {updatedAt && <span>{t('rawData.updated')} {fmtDt(updatedAt)}</span>}
    </div>
  );
}

function Pagination({ page, pages, total, onPage, t }) {
  if (pages <= 1) return null;
  const btns = [];
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, start + 4);
  for (let p = start; p <= end; p++) btns.push(p);
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-line">
      <div className="text-[11px] text-[var(--text-secondary)]">{t('rawData.pageOf', { page, pages })} ({fmt(total)})</div>
      <div className="flex gap-1">
        <button onClick={() => onPage(1)} disabled={page <= 1} className="px-2 py-1 text-[11px] rounded bg-surface-inset text-content-tertiary hover:bg-surface-active disabled:opacity-30">&laquo;</button>
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className="px-2 py-1 text-[11px] rounded bg-surface-inset text-content-tertiary hover:bg-surface-active disabled:opacity-30">&lsaquo;</button>
        {btns.map(p => (
          <button key={p} onClick={() => onPage(p)}
            className={`px-2 py-1 text-[11px] rounded ${p === page ? 'bg-indigo-600/30 text-indigo-300 font-bold' : 'bg-surface-inset text-content-tertiary hover:bg-surface-active'}`}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} className="px-2 py-1 text-[11px] rounded bg-surface-inset text-content-tertiary hover:bg-surface-active disabled:opacity-30">&rsaquo;</button>
        <button onClick={() => onPage(pages)} disabled={page >= pages} className="px-2 py-1 text-[11px] rounded bg-surface-inset text-content-tertiary hover:bg-surface-active disabled:opacity-30">&raquo;</button>
      </div>
    </div>
  );
}

function downloadCSV(filename, headers, rows) {
  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// client-side search filter helper
function matchesSearch(row, search, keys) {
  if (!search) return true;
  const s = search.toLowerCase();
  return keys.some(k => String(row[k] ?? '').toLowerCase().includes(s));
}

// client-side sort helper
function sortRows(rows, sortKey, sortDir) {
  return [...rows].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
    return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });
}

// ═══════════════════════════════════════════════════════════════════
// TAB: FORECAST (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabForecast({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kundeId, setKundeId] = useState('');
  const [datumVon, setDatumVon] = useState('');
  const [datumBis, setDatumBis] = useState('');
  const [status, setStatus] = useState('');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState('datum');
  const [order, setOrder] = useState('desc');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => { api('/api/rohdaten/kunden').then(setKunden).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 50, sort, order });
    if (kundeId) p.set('kunde_id', kundeId);
    if (datumVon) p.set('datum_von', datumVon);
    if (datumBis) p.set('datum_bis', datumBis);
    if (status) p.set('status', status);
    if (suche) p.set('suche', suche);
    api(`/api/rohdaten?${p}`).then(res => {
      setRows(res.rows); setTotal(res.total); setPages(res.pages); setUpdatedAt(new Date().toISOString());
    }).catch(() => { setRows([]); setTotal(0); setPages(0); }).finally(() => setLoading(false));
  }, [page, sort, order, kundeId, datumVon, datumBis, status, suche]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kundeId, datumVon, datumBis, status, suche]);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('desc'); } };

  const cols = [
    { key: 'datum', label: t('common.date'), sort: true },
    { key: 'kunde', label: t('common.customer'), sort: true },
    { key: 'soll', label: 'SOLL', sort: true, right: true },
    { key: 'ist', label: 'IST', sort: true, right: true },
    { key: 'diff', label: 'Diff', sort: false, right: true },
    { key: 'backlog', label: 'Backlog', sort: true, right: true },
    { key: 'status', label: t('common.status'), sort: true, center: true },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="db" />
        <TabMeta total={total} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('common.fullTextSearch')} />
        <FilterSelect value={kundeId} onChange={e => setKundeId(e.target.value)}>
          <option value="">{t('common.allCustomers')}</option>
          {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={datumVon} onChange={e => setDatumVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">–</span>
        <FilterInput type="date" value={datumBis} onChange={e => setDatumBis(e.target.value)} />
        <FilterSelect value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">{t('common.allStatus')}</option>
          <option value="entwurf">{t('rawData.statusDraft')}</option>
          <option value="bestaetigt">{t('rawData.statusConfirmed')}</option>
          <option value="verteilt">{t('rawData.statusDistributed')}</option>
        </FilterSelect>
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`forecast_${today()}.csv`,
          ['Datum', 'Kunde', 'Segment', 'SOLL', 'IST', 'Backlog', 'Status'],
          rows.map(r => [r.datum, `"${r.kunde_name}"`, r.segment, r.soll_pakete ?? '', r.ist_pakete ?? '', r.backlog_gesamt ?? '', r.forecast_status])
        )} />
      </div>

      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.right ? 'text-right' : c.center ? 'text-center' : 'text-left'} ${c.sort ? 'cursor-pointer hover:text-content-secondary select-none' : ''}`}
                    onClick={() => c.sort && handleSort(c.key)}>
                    {c.label}{c.sort && <SortIcon active={sort === c.key} dir={order} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('common.loadingData')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('common.noData')}</td></tr>
              : rows.map(r => {
                const soll = Number(r.soll_pakete) || 0;
                const ist = r.ist_pakete != null ? Number(r.ist_pakete) : null;
                const diff = ist != null ? ist - soll : null;
                const forecastStatusLabel = { entwurf: t('rawData.statusDraft'), bestaetigt: t('rawData.statusConfirmed'), verteilt: t('rawData.statusDistributed') };
                return (
                  <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                    <td className="py-2 px-3 font-mono text-content-tertiary">{fmtDate(r.datum)}</td>
                    <td className="py-2 px-3 text-content font-medium">{r.kunde_name} <span className="text-content-tertiary text-[10px]">{r.segment}</span></td>
                    <td className="py-2 px-3 text-right font-mono text-content-secondary">{fmt(soll)}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold text-content">{ist != null ? fmt(ist) : '–'}</td>
                    <td className="py-2 px-3 text-right font-mono text-content">{diff != null ? (diff >= 0 ? '+' : '') + fmt(diff) : '–'}</td>
                    <td className="py-2 px-3 text-right font-mono text-content-tertiary">{r.backlog_gesamt > 0 ? fmt(r.backlog_gesamt) : '–'}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.forecast_status === 'verteilt' ? 'bg-emerald-500/10 text-emerald-400' : r.forecast_status === 'bestaetigt' ? 'bg-primary-500/10 text-primary-400' : 'bg-gray-500/10 text-[var(--text-tertiary,#6B7280)]'}`}>
                        {forecastStatusLabel[r.forecast_status] || r.forecast_status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: AUFTRÄGE (Odoo stock.picking)
// ═══════════════════════════════════════════════════════════════════

function TabAuftraege({ t }) {
  const [allRows, setAllRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [von, setVon] = useState(monthStart());
  const [bis, setBis] = useState(today());
  const [statusF, setStatusF] = useState('alle');
  const [kundeF, setKundeF] = useState('alle');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState('create_date');
  const [order, setOrder] = useState('desc');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ von, bis, status: statusF, kunde: kundeF });
    api(`/api/rohdaten/auftraege?${p}`).then(res => {
      if (res.error) { setAllRows([]); setPartners([]); return; }
      setAllRows(res.rows || []); setPartners(res.partners || []); setUpdatedAt(res.updated_at);
    }).catch(() => setAllRows([])).finally(() => setLoading(false));
  }, [von, bis, statusF, kundeF]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [suche, sort, order]);

  const searchKeys = ['name', 'partner', 'state', 'origin', 'picking_type'];
  const filtered = useMemo(() => {
    const f = allRows.filter(r => matchesSearch(r, suche, searchKeys));
    return sortRows(f, sort, order);
  }, [allRows, suche, sort, order]);

  const pageSize = 50;
  const pages = Math.ceil(filtered.length / pageSize);
  const rows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('desc'); } };

  const stateLabel = { draft: t('rawData.draft'), waiting: t('rawData.waiting'), confirmed: t('rawData.confirmed'), assigned: t('rawData.ready'), done: t('rawData.done'), cancel: t('rawData.cancelled') };
  const stateColor = { done: 'bg-emerald-500/10 text-emerald-400', assigned: 'bg-primary-500/10 text-primary-400', confirmed: 'bg-yellow-500/10 text-yellow-400', draft: 'bg-gray-500/10 text-[var(--text-tertiary,#6B7280)]', waiting: 'bg-orange-500/10 text-orange-400', cancel: 'bg-red-500/10 text-red-400' };

  const cols = [
    { key: 'name', label: t('rawData.orderNo'), sort: true },
    { key: 'partner', label: t('common.customer'), sort: true },
    { key: 'state', label: t('common.status'), sort: true, center: true },
    { key: 'create_date', label: t('rawData.created'), sort: true },
    { key: 'date_done', label: t('rawData.completed'), sort: true },
    { key: 'moves_count', label: t('rawData.positions'), sort: true, right: true },
    { key: 'picking_type', label: t('rawData.type'), sort: true },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="odoo" />
        <TabMeta total={filtered.length} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('common.fullTextSearch')} />
        <FilterSelect value={kundeF} onChange={e => { setKundeF(e.target.value); }}>
          <option value="alle">{t('common.allCustomers')}</option>
          {partners.map(p => <option key={p} value={p}>{p}</option>)}
        </FilterSelect>
        <FilterSelect value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="alle">{t('common.allStatus')}</option>
          <option value="offen">{t('rawData.open')}</option>
          <option value="erledigt">{t('rawData.done')}</option>
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">–</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`auftraege_${today()}.csv`,
          ['Nr', 'Kunde', 'Status', 'Erstellt', 'Abgeschlossen', 'Positionen', 'Typ'],
          filtered.map(r => [r.name, `"${r.partner}"`, r.state, r.create_date || '', r.date_done || '', r.moves_count, `"${r.picking_type}"`])
        )} />
      </div>
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.right ? 'text-right' : c.center ? 'text-center' : 'text-left'} ${c.sort ? 'cursor-pointer hover:text-content-secondary select-none' : ''}`}
                    onClick={() => c.sort && handleSort(c.key)}>
                    {c.label}{c.sort && <SortIcon active={sort === c.key} dir={order} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('rawData.loadingFromOdoo')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('rawData.noOrders')}</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-3 font-mono text-content-secondary">{r.name}</td>
                  <td className="py-2 px-3 text-content font-medium">{r.partner}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${stateColor[r.state] || 'bg-gray-500/10 text-[var(--text-tertiary,#6B7280)]'}`}>{stateLabel[r.state] || r.state}</span>
                  </td>
                  <td className="py-2 px-3 font-mono text-content-tertiary text-[11px]">{fmtDt(r.create_date)}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary text-[11px]">{r.date_done ? fmtDt(r.date_done) : '–'}</td>
                  <td className="py-2 px-3 text-right font-mono text-content-secondary">{r.moves_count}</td>
                  <td className="py-2 px-3 text-content-tertiary">{r.picking_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={filtered.length} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: ANWESENHEITEN (Odoo hr.attendance)
// ═══════════════════════════════════════════════════════════════════

function TabAnwesenheiten({ t }) {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [von, setVon] = useState(today());
  const [bis, setBis] = useState(today());
  const [maF, setMaF] = useState('');
  const [deptF, setDeptF] = useState('');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState('check_in');
  const [order, setOrder] = useState('desc');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    api(`/api/odoo/anwesenheiten?von=${von}&bis=${bis}`).then(res => {
      if (res.error) { setAllRows([]); return; }
      setAllRows(res.records || []); setUpdatedAt(new Date().toISOString());
    }).catch(() => setAllRows([])).finally(() => setLoading(false));
  }, [von, bis]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [suche, maF, deptF, sort, order]);

  const departments = useMemo(() => [...new Set(allRows.map(r => r.department).filter(Boolean))].sort(), [allRows]);
  const employees = useMemo(() => [...new Set(allRows.map(r => r.employee_name).filter(Boolean))].sort(), [allRows]);

  const searchKeys = ['employee_name', 'department', 'job_title'];
  const filtered = useMemo(() => {
    let f = allRows.filter(r => matchesSearch(r, suche, searchKeys));
    if (maF) f = f.filter(r => r.employee_name === maF);
    if (deptF) f = f.filter(r => r.department === deptF);
    return sortRows(f, sort, order);
  }, [allRows, suche, maF, deptF, sort, order]);

  const pageSize = 50;
  const pages = Math.ceil(filtered.length / pageSize);
  const rows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('desc'); } };

  const cols = [
    { key: 'employee_name', label: t('common.employee'), sort: true },
    { key: 'check_in', label: t('rawData.checkIn'), sort: true },
    { key: 'check_out', label: t('rawData.checkOut'), sort: true },
    { key: 'worked_hours', label: t('rawData.hours'), sort: true, right: true },
    { key: 'department', label: t('rawData.department'), sort: true },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="odoo" />
        <TabMeta total={filtered.length} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('common.fullTextSearch')} />
        <FilterSelect value={maF} onChange={e => setMaF(e.target.value)}>
          <option value="">{t('rawData.allEmployees')}</option>
          {employees.map(e => <option key={e} value={e}>{e}</option>)}
        </FilterSelect>
        <FilterSelect value={deptF} onChange={e => setDeptF(e.target.value)}>
          <option value="">{t('rawData.allDepartments')}</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">–</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`anwesenheiten_${today()}.csv`,
          ['MA', 'Einstempeln', 'Ausstempeln', 'Stunden', 'Abteilung'],
          filtered.map(r => [`"${r.employee_name}"`, r.check_in || '', r.check_out || '', r.worked_hours ?? '', `"${r.department || ''}"`])
        )} />
      </div>
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.right ? 'text-right' : 'text-left'} cursor-pointer hover:text-content-secondary select-none`}
                    onClick={() => handleSort(c.key)}>
                    {c.label}<SortIcon active={sort === c.key} dir={order} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="py-10 text-center text-content-tertiary">{t('rawData.loadingFromOdoo')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-content-tertiary">{t('rawData.noEntries')}</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-3 text-content font-medium">
                    {r.employee_name}
                    {r.is_present && <span className="ml-2 w-1.5 h-1.5 rounded-full bg-green-400 inline-block" title={t('rawData.present')} />}
                  </td>
                  <td className="py-2 px-3 font-mono text-content-tertiary text-[11px]">{fmtDt(r.check_in)}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary text-[11px]">{r.check_out ? fmtDt(r.check_out) : <span className="text-green-400 text-[10px]">{t('rawData.active')}</span>}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-content">
                    {r.worked_hours != null ? r.worked_hours.toFixed(2) + 'h' : '–'}
                  </td>
                  <td className="py-2 px-3 text-content-tertiary">{r.department || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={filtered.length} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: BUCHHALTUNG (Odoo account.analytic.line)
// ═══════════════════════════════════════════════════════════════════

function TabBuchhaltung({ t }) {
  const [allRows, setAllRows] = useState([]);
  const [kostenstellen, setKostenstellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [von, setVon] = useState(monthStart());
  const [bis, setBis] = useState(today());
  const [ksF, setKsF] = useState('');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState('date');
  const [order, setOrder] = useState('desc');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    api(`/api/rohdaten/buchhaltung?von=${von}&bis=${bis}`).then(res => {
      if (res.error) { setAllRows([]); setKostenstellen([]); return; }
      setAllRows(res.rows || []); setKostenstellen(res.kostenstellen || []); setUpdatedAt(res.updated_at);
    }).catch(() => setAllRows([])).finally(() => setLoading(false));
  }, [von, bis]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [suche, ksF, sort, order]);

  const searchKeys = ['kostenstelle', 'name', 'ref', 'partner', 'account'];
  const filtered = useMemo(() => {
    let f = allRows.filter(r => matchesSearch(r, suche, searchKeys));
    if (ksF) f = f.filter(r => r.kostenstelle === ksF);
    return sortRows(f, sort, order);
  }, [allRows, suche, ksF, sort, order]);

  const pageSize = 50;
  const pages = Math.ceil(filtered.length / pageSize);
  const rows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('desc'); } };

  const cols = [
    { key: 'kostenstelle', label: t('rawData.costCenter'), sort: true },
    { key: 'date', label: t('common.date'), sort: true },
    { key: 'name', label: t('rawData.description'), sort: true },
    { key: 'soll', label: 'Soll', sort: true, right: true },
    { key: 'haben', label: 'Haben', sort: true, right: true },
    { key: 'amount', label: 'Saldo', sort: true, right: true },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="odoo" />
        <TabMeta total={filtered.length} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('common.fullTextSearch')} />
        <FilterSelect value={ksF} onChange={e => setKsF(e.target.value)}>
          <option value="">{t('rawData.allCostCenters')}</option>
          {kostenstellen.map(k => <option key={k} value={k}>{k}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">–</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`buchhaltung_${today()}.csv`,
          ['Kostenstelle', 'Datum', 'Bezeichnung', 'Soll', 'Haben', 'Saldo'],
          filtered.map(r => [`"${r.kostenstelle}"`, r.date, `"${r.name}"`, r.soll.toFixed(2), r.haben.toFixed(2), r.amount.toFixed(2)])
        )} />
      </div>
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.right ? 'text-right' : 'text-left'} cursor-pointer hover:text-content-secondary select-none`}
                    onClick={() => handleSort(c.key)}>
                    {c.label}<SortIcon active={sort === c.key} dir={order} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="py-10 text-center text-content-tertiary">{t('rawData.loadingFromOdoo')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-content-tertiary">{t('rawData.noBookings')}</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-3 text-content font-medium">{r.kostenstelle}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary">{fmtDate(r.date)}</td>
                  <td className="py-2 px-3 text-content-secondary truncate max-w-[300px]" title={r.name}>{r.name || '–'}</td>
                  <td className="py-2 px-3 text-right font-mono text-content-secondary">{r.soll > 0 ? fmtEur(r.soll) : '–'}</td>
                  <td className="py-2 px-3 text-right font-mono text-content-secondary">{r.haben > 0 ? fmtEur(r.haben) : '–'}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold text-content">{fmtEur(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={filtered.length} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: MITARBEITER (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabMitarbeiter({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');
  const [rolle, setRolle] = useState('');
  const [sort, setSort] = useState('name');
  const [order, setOrder] = useState('asc');
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 50, sort, order });
    if (suche) p.set('suche', suche);
    if (rolle) p.set('rolle', rolle);
    api(`/api/rohdaten/mitarbeiter-all?${p}`).then(res => {
      setRows(res.rows); setTotal(res.total); setPages(res.pages); setUpdatedAt(res.updated_at);
    }).catch(() => { setRows([]); setTotal(0); setPages(0); }).finally(() => setLoading(false));
  }, [page, sort, order, suche, rolle]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [suche, rolle]);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('asc'); } };

  const tage = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const tageKeys = ['arbeitstage_mo', 'arbeitstage_di', 'arbeitstage_mi', 'arbeitstage_do', 'arbeitstage_fr', 'arbeitstage_sa', 'arbeitstage_so'];
  const rolleLabel = { picker: 'Picker', packer: 'Packer', both: t('rawData.both') };
  const rolleColor = { picker: 'bg-primary-500/10 text-primary-400', packer: 'bg-purple-500/10 text-purple-400', both: 'bg-cyan-500/10 text-cyan-400' };

  const cols = [
    { key: 'name', label: 'Name', sort: true },
    { key: 'kuerzel', label: t('rawData.abbreviation'), sort: true },
    { key: 'rolle', label: t('rawData.role'), sort: true },
    { key: 'standort_name', label: t('rawData.location'), sort: false },
    { key: 'wochenstunden_soll', label: t('rawData.hoursPerWeek'), sort: true, right: true },
    { key: 'max_ueberstunden_woche', label: t('rawData.maxOvertime'), sort: false, right: true },
    { key: 'arbeitstage', label: t('rawData.workDays'), sort: false },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="db" />
        <TabMeta total={total} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('rawData.searchNameAbbr')} />
        <FilterSelect value={rolle} onChange={e => setRolle(e.target.value)}>
          <option value="">{t('rawData.allRoles')}</option>
          <option value="picker">Picker</option>
          <option value="packer">Packer</option>
          <option value="both">{t('rawData.both')}</option>
        </FilterSelect>
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`mitarbeiter_${today()}.csv`,
          ['Name', t('rawData.abbreviation'), t('rawData.role'), t('rawData.location'), t('rawData.hoursPerWeek'), t('rawData.maxOvertime'), t('rawData.workDays')],
          rows.map(r => [`"${r.name}"`, r.kuerzel, r.rolle, `"${r.standort_name || ''}"`, Number(r.wochenstunden_soll), Number(r.max_ueberstunden_woche), tageKeys.filter(k => r[k]).length])
        )} />
      </div>
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.right ? 'text-right' : 'text-left'} ${c.sort ? 'cursor-pointer hover:text-content-secondary select-none' : ''}`}
                    onClick={() => c.sort && handleSort(c.key)}>
                    {c.label}{c.sort && <SortIcon active={sort === c.key} dir={order} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('common.loadingData')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('rawData.noEmployees')}</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-3 text-content font-medium">{r.name}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary">{r.kuerzel}</td>
                  <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded font-medium ${rolleColor[r.rolle] || ''}`}>{rolleLabel[r.rolle] || r.rolle}</span></td>
                  <td className="py-2 px-3 text-content-tertiary">{r.standort_name || '–'}</td>
                  <td className="py-2 px-3 text-right font-mono text-content-secondary">{Number(r.wochenstunden_soll)}</td>
                  <td className="py-2 px-3 text-right font-mono text-content-secondary">{Number(r.max_ueberstunden_woche)}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      {tage.map((d, i) => <span key={d} className={`w-5 h-4 flex items-center justify-center rounded text-[9px] font-bold ${r[tageKeys[i]] ? 'bg-surface-active text-content' : 'text-content-tertiary'}`}>{d}</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: SCHICHTPLANUNG (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabSchichtplanung({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [datumVon, setDatumVon] = useState('');
  const [datumBis, setDatumBis] = useState('');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState('datum');
  const [order, setOrder] = useState('desc');
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 50, sort, order });
    if (datumVon) p.set('datum_von', datumVon);
    if (datumBis) p.set('datum_bis', datumBis);
    if (suche) p.set('suche', suche);
    api(`/api/rohdaten/schichtplanung?${p}`).then(res => {
      setRows(res.rows); setTotal(res.total); setPages(res.pages); setUpdatedAt(res.updated_at);
    }).catch(() => { setRows([]); setTotal(0); setPages(0); }).finally(() => setLoading(false));
  }, [page, sort, order, datumVon, datumBis, suche]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [datumVon, datumBis, suche]);

  const handleSort = (k) => { if (sort === k) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(k); setOrder('desc'); } };

  const rolleLabel = { picker: 'Picker', packer: 'Packer', both: t('rawData.both') };
  const statusLabel = { geplant: t('rawData.planned'), bestaetigt: t('rawData.confirmed'), abgesagt: t('rawData.cancelled') };
  const statusColor = { geplant: 'bg-gray-500/10 text-[var(--text-tertiary,#6B7280)]', bestaetigt: 'bg-emerald-500/10 text-emerald-400', abgesagt: 'bg-red-500/10 text-red-400' };

  const cols = [
    { key: 'datum', label: t('common.date'), sort: true },
    { key: 'ma_name', label: t('common.employee'), sort: true },
    { key: 'schicht_name', label: t('rawData.shift'), sort: true },
    { key: 'start_zeit', label: t('common.from'), sort: false },
    { key: 'end_zeit', label: t('common.to'), sort: false },
    { key: 'rolle', label: t('rawData.role'), sort: true },
    { key: 'status', label: t('common.status'), sort: true, center: true },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SourceBadge type="db" />
        <TabMeta total={total} updatedAt={updatedAt} t={t} />
        <div className="flex-1" />
        <SearchInput value={suche} onChange={setSuche} placeholder={t('rawData.searchShift')} />
        <FilterInput type="date" value={datumVon} onChange={e => setDatumVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">–</span>
        <FilterInput type="date" value={datumBis} onChange={e => setDatumBis(e.target.value)} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={() => downloadCSV(`schichtplanung_${today()}.csv`,
          [t('common.date'), t('common.employee'), t('rawData.abbreviation'), t('rawData.shift'), t('common.from'), t('common.to'), t('rawData.role'), t('common.status')],
          rows.map(r => [r.datum, `"${r.ma_name}"`, r.ma_kuerzel, `"${r.schicht_name}"`, r.start_zeit?.slice(0, 5) || '', r.end_zeit?.slice(0, 5) || '', r.rolle, r.status])
        )} />
      </div>
      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                {cols.map(c => (
                  <th key={c.key} className={`py-2.5 px-3 ${c.center ? 'text-center' : 'text-left'} ${c.sort ? 'cursor-pointer hover:text-content-secondary select-none' : ''}`}
                    onClick={() => c.sort && handleSort(c.key)}>
                    {c.label}{c.sort && <SortIcon active={sort === c.key} dir={order} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('common.loadingData')}</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-content-tertiary">{t('rawData.noShiftData')}</td></tr>
              : rows.map(r => (
                <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-3 font-mono text-content-tertiary">{fmtDate(r.datum)}</td>
                  <td className="py-2 px-3 text-content font-medium">{r.ma_name} <span className="text-content-tertiary text-[10px]">{r.ma_kuerzel}</span></td>
                  <td className="py-2 px-3 text-content-secondary">{r.schicht_name}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary">{r.start_zeit?.slice(0, 5) || '–'}</td>
                  <td className="py-2 px-3 font-mono text-content-tertiary">{r.end_zeit?.slice(0, 5) || '–'}</td>
                  <td className="py-2 px-3 text-content-secondary">{rolleLabel[r.rolle] || r.rolle}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor[r.status] || 'bg-gray-500/10 text-[var(--text-tertiary,#6B7280)]'}`}>{statusLabel[r.status] || r.status || 'geplant'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB CONFIG
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// TAB: BILLING (deckungsbeitrag_cache billing_lines)
// ═══════════════════════════════════════════════════════════════════

function TabBilling({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [kunden, setKunden] = useState([]);
  const [kundeId, setKundeId] = useState('');
  const [von, setVon] = useState(monthStart());
  const [bis, setBis] = useState(today());
  const [produkt, setProdukt] = useState('');
  const [rechnung, setRechnung] = useState('');
  const [sortBy, setSortBy] = useState('billing_date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetch('/api/rohdaten/filter-options', { credentials: 'include' }).then(r => r.json()).then(d => setKunden(d.kunden || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 100, sort_by: sortBy, sort_dir: sortDir });
    if (kundeId) p.set('kunde_id', kundeId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (produkt) p.set('produkt', produkt);
    if (rechnung) p.set('rechnung', rechnung);
    fetch(`/api/rohdaten/billing?${p}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => { setRows(res.rows || []); setTotal(res.total || 0); setPages(res.pages || 0); setKpi(res.kpi || {}); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, sortBy, sortDir, kundeId, von, bis, produkt, rechnung]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kundeId, von, bis, produkt, rechnung]);

  const handleSort = (k) => { if (sortBy === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortBy(k); setSortDir('desc'); } };

  const exportCSV = () => {
    const p = new URLSearchParams({ format: 'csv' });
    if (kundeId) p.set('kunde_id', kundeId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (produkt) p.set('produkt', produkt);
    if (rechnung) p.set('rechnung', rechnung);
    window.open(`/api/rohdaten/billing?${p}`, '_blank');
  };

  const katColor = { handling: 'text-primary-400 bg-primary-500/10', verpackung: 'text-purple-400 bg-purple-500/10', versand: 'text-orange-400 bg-orange-500/10', ignorieren: 'text-[var(--text-secondary,#4B5563)] bg-gray-500/10' };

  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-3 p-4 border-b border-line bg-surface-inset/30">
        {[
          { label: t('rawData.totalLines'), value: fmt(kpi.zeilen), color: 'text-primary-400' },
          { label: t('rawData.totalQuantity'), value: fmt(kpi.summe_menge), color: 'text-emerald-400' },
          { label: t('rawData.totalRevenue'), value: fmtEur(kpi.summe_umsatz), color: 'text-amber-400' },
          { label: t('rawData.invoices'), value: fmt(kpi.eindeutige_rechnungen), color: 'text-purple-400' },
          { label: t('common.customers'), value: fmt(kpi.eindeutige_kunden), color: 'text-indigo-400' },
        ].map(c => (
          <div key={c.label} className="text-center">
            <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{c.label}</div>
            <div className={`font-mono font-bold text-lg ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line">
        <FilterSelect value={kundeId} onChange={e => setKundeId(e.target.value)}>
          <option value="">{t('common.allCustomers')}</option>
          {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">{t('common.to')}</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <SearchInput value={produkt} onChange={setProdukt} placeholder={t('rawData.searchProduct')} />
        <SearchInput value={rechnung} onChange={setRechnung} placeholder={t('rawData.searchInvoice')} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={exportCSV} />
        <div className="ml-auto"><TabMeta total={total} t={t} /></div>
      </div>

      {/* Tabelle */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-surface-inset text-content-tertiary">
              {[
                { k: 'billing_date', l: t('common.date'), w: 'w-24' },
                { k: 'picking_name', l: t('rawData.invoice'), w: 'w-36' },
                { k: 'kunde', l: t('common.customer'), w: 'w-40' },
                { k: 'produkt', l: t('rawData.productDescription'), w: '' },
                { k: 'kategorie', l: t('rawData.category'), w: 'w-24' },
                { k: 'menge', l: t('rawData.quantity'), w: 'w-20 text-right' },
                { k: 'umsatz', l: t('rawData.revenue'), w: 'w-24 text-right' },
              ].map(h => (
                <th key={h.k} className={`px-3 py-2 font-medium text-left cursor-pointer hover:text-content select-none ${h.w}`} onClick={() => handleSort(h.k)}>
                  {h.l}<SortIcon active={sortBy === h.k} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-content-tertiary">{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-content-tertiary">{t('common.noData')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-line hover:bg-surface-hover transition-colors">
                <td className="px-3 py-2 font-mono text-[var(--text-secondary)]">{fmtDate(r.billing_date)}</td>
                <td className="px-3 py-2 font-mono text-content-tertiary truncate max-w-[200px]" title={r.picking_name}>{r.picking_name}</td>
                <td className="px-3 py-2 text-content-secondary truncate max-w-[180px]">{r.kunde_name}</td>
                <td className="px-3 py-2 text-content">{r.name}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${katColor[r.kategorie] || ''}`}>{r.kategorie}</span></td>
                <td className="px-3 py-2 text-right font-mono text-content-secondary">{fmt(r.quantity)}</td>
                <td className="px-3 py-2 text-right font-mono text-content-secondary">{fmtEur(r.umsatz)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: WAVES (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabWaves({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ kunden: [], mitarbeiter: [] });
  const [kundeId, setKundeId] = useState('');
  const [maId, setMaId] = useState('');
  const [von, setVon] = useState(monthStart());
  const [bis, setBis] = useState(today());
  const [waveName, setWaveName] = useState('');
  const [sortBy, setSortBy] = useState('datum');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetch('/api/rohdaten/filter-options', { credentials: 'include' }).then(r => r.json()).then(setOpts).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 100, sort_by: sortBy, sort_dir: sortDir });
    if (kundeId) p.set('kunde_id', kundeId);
    if (maId) p.set('ma_id', maId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (waveName) p.set('wave_name', waveName);
    fetch(`/api/rohdaten/waves?${p}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => { setRows(res.rows || []); setTotal(res.total || 0); setPages(res.pages || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, sortBy, sortDir, kundeId, maId, von, bis, waveName]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kundeId, maId, von, bis, waveName]);

  const handleSort = (k) => { if (sortBy === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortBy(k); setSortDir('desc'); } };

  const exportCSV = () => {
    const p = new URLSearchParams({ format: 'csv' });
    if (kundeId) p.set('kunde_id', kundeId);
    if (maId) p.set('ma_id', maId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (waveName) p.set('wave_name', waveName);
    window.open(`/api/rohdaten/waves?${p}`, '_blank');
  };

  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line">
        <FilterSelect value={kundeId} onChange={e => setKundeId(e.target.value)}>
          <option value="">{t('common.allCustomers')}</option>
          {(opts.kunden || []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </FilterSelect>
        <FilterSelect value={maId} onChange={e => setMaId(e.target.value)}>
          <option value="">{t('rawData.allEmployees')}</option>
          {(opts.mitarbeiter || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">{t('common.to')}</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <SearchInput value={waveName} onChange={setWaveName} placeholder="Wave-Name..." />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={exportCSV} />
        <div className="ml-auto"><TabMeta total={total} t={t} /></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-surface-inset text-content-tertiary">
              {[
                { k: 'datum', l: t('common.date') }, { k: 'wave_name', l: 'Wave' }, { k: 'kunde', l: t('common.customer') },
                { k: 'ma', l: t('common.employee') }, { k: 'picking_name', l: 'Picking' },
                { k: 'pickings', l: 'Pickings' }, { k: 'artikel', l: t('rawData.articles') }, { k: 'sku_count', l: 'SKUs' },
                { k: 'dauer', l: t('rawData.durationMin') },
              ].map(h => (
                <th key={h.k} className="px-3 py-2 font-medium text-left cursor-pointer hover:text-content select-none" onClick={() => handleSort(h.k)}>
                  {h.l}<SortIcon active={sortBy === h.k} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-content-tertiary">{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-content-tertiary">{t('common.noData')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-line hover:bg-surface-hover transition-colors">
                <td className="px-3 py-2 font-mono text-[var(--text-secondary)]">{fmtDate(r.datum)}</td>
                <td className="px-3 py-2 text-content truncate max-w-[180px]" title={r.wave_name}>{r.wave_name}{r.is_sop && <span className="ml-1 text-[9px] bg-amber-500/15 text-amber-400 px-1 rounded">SOP</span>}</td>
                <td className="px-3 py-2 text-content-secondary truncate max-w-[160px]">{r.kunde_name}</td>
                <td className="px-3 py-2 text-content-secondary">{r.ma_name || '–'}</td>
                <td className="px-3 py-2 font-mono text-content-tertiary text-[11px] truncate max-w-[140px]">{r.picking_name}</td>
                <td className="px-3 py-2 text-right font-mono">{r.pickings ?? '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.article_count ?? '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.sku_count ?? '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.duration_minutes != null ? Number(r.duration_minutes).toFixed(1) : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: PACKS (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabPacks({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ kunden: [], mitarbeiter: [] });
  const [kundeId, setKundeId] = useState('');
  const [maId, setMaId] = useState('');
  const [von, setVon] = useState(monthStart());
  const [bis, setBis] = useState(today());
  const [sortBy, setSortBy] = useState('start_time');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetch('/api/rohdaten/filter-options', { credentials: 'include' }).then(r => r.json()).then(setOpts).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 100, sort_by: sortBy, sort_dir: sortDir });
    if (kundeId) p.set('kunde_id', kundeId);
    if (maId) p.set('ma_id', maId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    fetch(`/api/rohdaten/packs?${p}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => { setRows(res.rows || []); setTotal(res.total || 0); setPages(res.pages || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, sortBy, sortDir, kundeId, maId, von, bis]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kundeId, maId, von, bis]);

  const handleSort = (k) => { if (sortBy === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortBy(k); setSortDir('desc'); } };

  const exportCSV = () => {
    const p = new URLSearchParams({ format: 'csv' });
    if (kundeId) p.set('kunde_id', kundeId);
    if (maId) p.set('ma_id', maId);
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    window.open(`/api/rohdaten/packs?${p}`, '_blank');
  };

  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line">
        <FilterSelect value={kundeId} onChange={e => setKundeId(e.target.value)}>
          <option value="">{t('common.allCustomers')}</option>
          {(opts.kunden || []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </FilterSelect>
        <FilterSelect value={maId} onChange={e => setMaId(e.target.value)}>
          <option value="">{t('rawData.allEmployees')}</option>
          {(opts.mitarbeiter || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </FilterSelect>
        <FilterInput type="date" value={von} onChange={e => setVon(e.target.value)} />
        <span className="text-content-tertiary text-xs">{t('common.to')}</span>
        <FilterInput type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={exportCSV} />
        <div className="ml-auto"><TabMeta total={total} t={t} /></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-surface-inset text-content-tertiary">
              {[
                { k: 'start_time', l: t('rawData.dateTime') }, { k: 'picking_name', l: 'Picking' }, { k: 'kunde', l: t('common.customer') },
                { k: 'ma', l: 'Packer' }, { k: 'dauer', l: t('rawData.durationMin') },
                { k: 'artikel', l: t('rawData.articles') }, { k: 'sku_count', l: 'SKUs' }, { k: 'package_count', l: t('rawData.packages') },
              ].map(h => (
                <th key={h.k} className="px-3 py-2 font-medium text-left cursor-pointer hover:text-content select-none" onClick={() => handleSort(h.k)}>
                  {h.l}<SortIcon active={sortBy === h.k} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-content-tertiary">{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-content-tertiary">{t('common.noData')}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-line hover:bg-surface-hover transition-colors">
                <td className="px-3 py-2 font-mono text-[var(--text-secondary)]">{fmtDt(r.start_time)}</td>
                <td className="px-3 py-2 font-mono text-content-tertiary text-[11px] truncate max-w-[160px]">{r.picking_name}</td>
                <td className="px-3 py-2 text-content-secondary truncate max-w-[160px]">{r.kunde_name || r.merchant_name || '–'}</td>
                <td className="px-3 py-2 text-content-secondary">{r.ma_name || '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.duration_minutes != null ? Number(r.duration_minutes).toFixed(1) : '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.article_count ?? '–'}</td>
                <td className="px-3 py-2 text-right font-mono">{r.sku_count ?? '–'}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-content">{r.package_count ?? '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: KUNDEN (eigene DB)
// ═══════════════════════════════════════════════════════════════════

function TabKunden({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');
  const [segment, setSegment] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 100, sort_by: sortBy, sort_dir: sortDir });
    if (suche) p.set('suche', suche);
    if (segment) p.set('segment', segment);
    fetch(`/api/rohdaten/kunden-alle?${p}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => { setRows(res.rows || []); setTotal(res.total || 0); setPages(res.pages || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, sortBy, sortDir, suche, segment]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [suche, segment]);

  const handleSort = (k) => { if (sortBy === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortBy(k); setSortDir('asc'); } };

  const exportCSV = () => {
    const p = new URLSearchParams({ format: 'csv' });
    if (suche) p.set('suche', suche);
    if (segment) p.set('segment', segment);
    window.open(`/api/rohdaten/kunden-alle?${p}`, '_blank');
  };

  const segColor = { A: 'text-primary-400 bg-primary-500/10', B: 'text-purple-400 bg-purple-500/10', C: 'text-amber-400 bg-amber-500/10' };

  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line">
        <SearchInput value={suche} onChange={setSuche} placeholder={t('rawData.searchCustomer')} />
        <FilterSelect value={segment} onChange={e => setSegment(e.target.value)}>
          <option value="">{t('rawData.allSegments')}</option>
          <option value="A">A</option><option value="B">B</option><option value="C">C</option>
        </FilterSelect>
        <RefreshBtn onClick={load} loading={loading} t={t} />
        <ExportBtn onClick={exportCSV} />
        <div className="ml-auto"><TabMeta total={total} t={t} /></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-surface-inset text-content-tertiary">
              {[
                { k: 'name', l: 'Name' }, { k: 'segment', l: 'Segment' }, { k: 'odoo_partner_id', l: 'Odoo Partner ID' },
                { k: 'aktiv', l: t('common.status') }, { k: 'created_at', l: t('rawData.created') },
              ].map(h => (
                <th key={h.k} className="px-3 py-2 font-medium text-left cursor-pointer hover:text-content select-none" onClick={() => handleSort(h.k)}>
                  {h.l}<SortIcon active={sortBy === h.k} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-content-tertiary">{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-content-tertiary">{t('common.noData')}</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t border-line hover:bg-surface-hover transition-colors">
                <td className="px-3 py-2 text-content font-medium">{r.name}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${segColor[r.segment] || 'text-[var(--text-tertiary,#6B7280)]'}`}>{r.segment || '–'}</span></td>
                <td className="px-3 py-2 font-mono text-content-tertiary">{r.odoo_partner_id || '–'}</td>
                <td className="px-3 py-2">{r.aktiv ? <span className="text-emerald-400">{t('rawData.active')}</span> : <span className="text-red-400">{t('rawData.inactive')}</span>}</td>
                <td className="px-3 py-2 font-mono text-[var(--text-secondary)]">{fmtDate(r.created_at?.slice(0, 10))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={pages} total={total} onPage={setPage} t={t} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB: FORECAST SNAPSHOTS (Intraday Polling Data)
// ═══════════════════════════════════════════════════════════════════

function TabForecastSnapshots({ t }) {
  const [snapshots, setSnapshots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datum, setDatum] = useState(today());
  const [kundeId, setKundeId] = useState('');
  const [kunden, setKunden] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ datum });
    if (kundeId) params.set('kunde_id', kundeId);
    api(`/api/rohdaten/forecast-snapshots?${params}`)
      .then(d => { setSnapshots(d.snapshots || []); setSummary(d.summary || null); setLastUpdate(new Date()); })
      .catch(() => { setSnapshots([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [datum, kundeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/api/rohdaten/filter-options').then(d => setKunden(d.kunden || [])).catch(() => {});
  }, []);

  const handleCSV = () => {
    const params = new URLSearchParams({ datum, format: 'csv' });
    if (kundeId) params.set('kunde_id', kundeId);
    window.open(`/api/rohdaten/forecast-snapshots?${params}`, '_blank');
  };

  return (
    <div className="bg-surface-card border border-line rounded-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-line">
        <div className="flex items-center gap-3">
          <SourceBadge type="db" />
          <span className="text-sm font-semibold text-content">{t('rohdaten.forecastSnapshots')}</span>
          <span className="text-[11px] text-[var(--text-secondary)]">{t('rohdaten.forecastSnapshotsDesc')}</span>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && !loading && (
            <span className="text-[10px] text-[var(--text-tertiary)]">
              {snapshots.length} {t('rawData.entries')} · {lastUpdate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-surface-inset text-content-secondary hover:bg-surface-active border border-line transition-colors disabled:opacity-50">
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className={loading ? 'animate-spin' : ''}><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {t('common.refresh') || 'Refresh'}
          </button>
          <button onClick={handleCSV} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-surface-inset text-content-secondary hover:bg-surface-active border border-line transition-colors">
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            CSV
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-line bg-surface-inset/50">
        <FilterInput type="date" value={datum} onChange={e => setDatum(e.target.value)} />
        <select value={kundeId} onChange={e => setKundeId(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-xs bg-surface-card border border-line text-content focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
          <option value="">{t('common.allCustomers')}</option>
          {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-3 px-4 py-4 border-b border-line">
          {[
            { label: t('rohdaten.totalCustomers'), value: summary.total_kunden, icon: Users, color: '#64748B' },
            { label: t('rohdaten.pickingsDone'), value: summary.total_done, icon: PackageCheck, color: '#64748B' },
            { label: t('rohdaten.pickingsOpen'), value: summary.total_todo, icon: PackageX, color: '#64748B' },
            { label: t('rohdaten.snapshotsToday'), value: summary.snapshots_heute, icon: Camera, color: '#64748B' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
            <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] px-6 py-5 text-center">
              <div className="flex items-center justify-center gap-2">
                <Icon size={16} className="text-[#64748B]" />
                <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>{s.label}</div>
              </div>
              <div className="font-mono text-[28px] font-bold text-[var(--text-primary)] mt-1" style={{ letterSpacing: '-0.5px' }}>{fmt(s.value)}</div>
            </div>
          ); })}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" style={{ borderBottom: '1px solid #F3F4F6' }}>
              <th className="px-4 py-2.5 text-left font-semibold">{t('common.time')}</th>
              <th className="px-4 py-2.5 text-left font-semibold">{t('common.customer')}</th>
              <th className="px-4 py-2.5 text-right font-semibold">{t('rohdaten.pickingsDone')}</th>
              <th className="px-4 py-2.5 text-right font-semibold">{t('rohdaten.pickingsOpen')}</th>
              <th className="px-4 py-2.5 text-right font-semibold">{t('common.total')}</th>
              <th className="px-4 py-2.5 text-left font-semibold">{t('rohdaten.progress')}</th>
            </tr>
          </thead>
          <tbody>
            {snapshots.length === 0 && !loading && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-[var(--text-secondary)]">{t('common.noData')}</td></tr>
            )}
            {snapshots.map(s => {
              const total = s.pickings_done + s.pickings_todo;
              const pct = total > 0 ? Math.round((s.pickings_done / total) * 100) : 0;
              return (
                <tr key={s.id} className="hover:bg-[var(--bg-card-hover)] transition-colors" style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td className="px-4 py-2 text-[var(--text-tertiary)] font-mono">{fmtDt(s.fetched_at)}</td>
                  <td className="px-4 py-2 text-[var(--text-primary)] font-medium">{s.kunde_name}</td>
                  <td className="px-4 py-2 text-right font-mono text-[var(--text-primary)]">{fmt(s.pickings_done)}</td>
                  <td className="px-4 py-2 text-right font-mono text-[var(--text-primary)]">{fmt(s.pickings_todo)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-[var(--text-primary)]">{fmt(total)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div style={{ width: 60, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB' }}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, backgroundColor: '#338edc', transition: 'width 0.3s ease' }} />
                      </div>
                      <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TAB_DEFS = [
  { key: 'billing', labelKey: 'rawData.tabBilling', type: 'db', icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' },
  { key: 'waves', labelKey: 'rawData.tabWaves', type: 'db', icon: 'M3 3v18h18 M7 16l4-4 4 4 5-8' },
  { key: 'packs', labelKey: 'rawData.tabPacks', type: 'db', icon: 'M20 12V6a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6m16 0H4' },
  { key: 'forecast', labelKey: 'rawData.tabForecast', type: 'db', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
  { key: 'forecast-snapshots', labelKey: 'rohdaten.forecastSnapshots', type: 'db', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'anwesenheiten', labelKey: 'rawData.tabAttendance', type: 'odoo', icon: 'M12 6v6l4 2 M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'mitarbeiter', labelKey: 'rawData.tabEmployees', type: 'db', icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 3a4 4 0 100 8 4 4 0 000-8z' },
  { key: 'kunden', labelKey: 'rawData.tabCustomers', type: 'db', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
];

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════

export default function Rohdaten() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('billing');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api('/api/rohdaten/overview').then(setOverview).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-6">
        <NavLink to="/" className="text-[var(--text-secondary)] hover:text-content-secondary transition-colors">Home</NavLink>
        <span className="text-content-tertiary">/</span>
        <span className="text-indigo-400">{t('rawData.title')}</span>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {/* Eigene DB */}
        <div className="bg-surface-card border border-primary-500/10 rounded-card p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary-500/15 flex items-center justify-center">
              <svg width="18" height="18" fill="none" stroke="#338edc" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-bold text-content">{t('rawData.ownDatabase')}</div>
              <div className="text-[11px] text-[var(--text-secondary)]">PostgreSQL</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('rawData.tables')}</div>
              <div className="font-mono font-bold text-primary-400">{overview?.db?.tables ?? '–'}</div>
            </div>
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('rawData.records')}</div>
              <div className="font-mono font-bold text-primary-400">{overview?.db?.total_rows ? fmt(overview.db.total_rows) : '–'}</div>
            </div>
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('rawData.dbSize')}</div>
              <div className="font-mono font-bold text-primary-400">{overview?.db?.size ?? '–'}</div>
            </div>
          </div>
        </div>

        {/* Odoo */}
        <div className="bg-surface-card border border-orange-500/10 rounded-card p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center">
              <svg width="18" height="18" fill="none" stroke="#f97316" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-bold text-content">Odoo ERP</div>
              <div className="text-[11px] text-[var(--text-secondary)]">XMLRPC · READ-ONLY</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('common.status')}</div>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${overview?.odoo?.status === 'verbunden' ? 'bg-green-400' : overview?.odoo?.status === 'fehler' ? 'bg-red-400' : 'bg-yellow-400'}`} />
                <span className={`font-mono text-sm font-bold ${overview?.odoo?.status === 'verbunden' ? 'text-green-400' : overview?.odoo?.status === 'fehler' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {overview?.odoo?.status === 'verbunden' ? t('rawData.connected') : overview?.odoo?.status === 'fehler' ? t('rawData.error') : overview?.odoo?.status === 'konfiguriert' ? t('rawData.configured') : t('rawData.notConfigured')}
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">URL</div>
              <div className="font-mono text-xs text-orange-400 truncate">{overview?.odoo?.url || '–'}</div>
            </div>
            <div>
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Models</div>
              <div className="font-mono text-xs text-orange-400">stock.picking, hr.attendance, account.analytic.line</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-1 mb-5 bg-surface-card border border-line rounded-card p-1 overflow-x-auto shadow-card">
        {TAB_DEFS.map(td => (
          <button key={td.key} onClick={() => setTab(td.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs transition-colors whitespace-nowrap ${
              tab === td.key
                ? 'bg-white dark:bg-surface-elevated text-content shadow-sm font-semibold'
                : 'text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-inset font-medium'
            }`}>
            <svg width="14" height="14" fill="none" stroke={tab === td.key ? (td.type === 'db' ? '#338edc' : '#f97316') : 'currentColor'} strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d={td.icon} />
            </svg>
            {t(td.labelKey)}
            <span className={`w-1.5 h-1.5 rounded-full ${td.type === 'db' ? 'bg-primary-500' : 'bg-orange-500'}`} />
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'billing' && <TabBilling t={t} />}
      {tab === 'waves' && <TabWaves t={t} />}
      {tab === 'packs' && <TabPacks t={t} />}
      {tab === 'forecast' && <TabForecast t={t} />}
      {tab === 'forecast-snapshots' && <TabForecastSnapshots t={t} />}
      {tab === 'anwesenheiten' && <TabAnwesenheiten t={t} />}
      {tab === 'mitarbeiter' && <TabMitarbeiter t={t} />}
      {tab === 'kunden' && <TabKunden t={t} />}
    </div>
  );
}
